import type { NotificationHandle } from 'element-plus/lib/components/notification'
import { ElMessage } from 'element-plus'
import type { FeedNotificationOptions } from '~/stores/notificationFeed'
import { useNotificationFeedStore } from '~/stores/notificationFeed'
import { useUserStore } from '~/stores/user'

const DURATION_BY_TYPE = {
  success: 4000, // 4s - Quick confirmation
  info: 5000, // 5s - Informational
  warning: 7000, // 7s - Needs attention
  error: 10000, // 10s - Critical, needs action
} as const

const DEFAULT_DURATION = 5000 // 5s default

class NotificationService {
  private getFeedStore() {
    return useNotificationFeedStore()
  }

  /**
   * Shows a notification in the bell icon feed.
   * Duration varies by type: success (4s), info (5s), warning (7s), error (10s).
   */
  show(options: FeedNotificationOptions): NotificationHandle & { id: string } {
    const duration =
      options.duration ??
      (options.type && options.type in DURATION_BY_TYPE
        ? DURATION_BY_TYPE[options.type as keyof typeof DURATION_BY_TYPE]
        : DEFAULT_DURATION)

    const notificationOptions: FeedNotificationOptions = {
      ...options,
      duration,
    }

    return this.displayNotification(notificationOptions)
  }

  /**
   * Displays a notification.
   *
   * When the user is logged in: adds to the bell feed store (visible via NotificationBell).
   * When the user is not logged in: falls back to an ElMessage toast, since the bell
   * isn't rendered on public pages (login, sign-up, password reset, etc.).
   */
  private displayNotification(
    options: FeedNotificationOptions
  ): NotificationHandle & { id: string } {
    // Bell isn't mounted on public pages — show a dismissible toast instead.
    if (!useUserStore().loggedIn) {
      const messageHandle = ElMessage({
        // Most callers pass only `title` — the bell feed renders it, but ElMessage has
        // no title of its own, so reading `message` alone rendered an empty toast with
        // nothing but an icon. Fall back so the text survives either display path.
        message: options.message || options.title,
        // NotificationType includes '' (no type) — normalize to undefined so ElMessage is happy
        type: (options.type || undefined) as
          'success' | 'warning' | 'info' | 'error' | undefined,
        duration: options.duration,
        showClose: true,
        grouping: true,
        // Header.vue sits at z-index:3000, which covers ElMessage's default z-index (~2000).
        // Offsetting past the 58px header keeps pre-auth toasts visible.
        offset: 70,
        onClose: options.onClose as () => void,
      })
      return {
        id: '',
        close: () => messageHandle.close(),
      } as NotificationHandle & { id: string }
    }

    const originalOnClose = options.onClose

    // Add to notification feed store (this is the primary display method)
    this.getFeedStore().addNotification(options)

    // Get the actual notification ID from the most recent notification
    // (since addNotification creates the ID internally, we need to retrieve it)
    const mostRecent = this.getFeedStore().mostRecent
    if (!mostRecent) {
      // This should not happen, but as a safeguard:
      console.error('Could not retrieve notification after adding it.')
      return { id: '', close: () => {} } as NotificationHandle & { id: string }
    }
    const actualId = mostRecent.id

    // Create a proxy handle that mimics ElNotification but doesn't actually display
    // This maintains API compatibility while using the new feed system
    const handle = {
      id: actualId,
      close: () => {
        ;(originalOnClose as () => void)?.()
      },
    } as NotificationHandle & { id: string }

    return handle
  }

  /**
   * Updates an existing notification's message
   */
  updateNotification(
    id: string,
    updates: { message?: string; title?: string }
  ): void {
    this.getFeedStore().updateNotification(id, updates)
  }

  /**
   * Convenience method for success notifications
   */
  success(
    options: FeedNotificationOptions | string
  ): NotificationHandle & { id: string } {
    if (typeof options === 'string') {
      return this.show({ message: options, type: 'success' })
    }
    return this.show({ ...options, type: 'success' })
  }

  /**
   * Convenience method for error notifications
   */
  error(
    options: FeedNotificationOptions | string
  ): NotificationHandle & { id: string } {
    if (typeof options === 'string') {
      return this.show({ message: options, type: 'error' })
    }
    return this.show({ ...options, type: 'error' })
  }

  /**
   * Convenience method for warning notifications
   */
  warning(
    options: FeedNotificationOptions | string
  ): NotificationHandle & { id: string } {
    if (typeof options === 'string') {
      return this.show({ message: options, type: 'warning' })
    }
    return this.show({ ...options, type: 'warning' })
  }

  /**
   * Convenience method for info notifications
   */
  info(
    options: FeedNotificationOptions | string
  ): NotificationHandle & { id: string } {
    if (typeof options === 'string') {
      return this.show({ message: options, type: 'info' })
    }
    return this.show({ ...options, type: 'info' })
  }

  /**
   * Clears all notifications from the feed
   */
  closeAll() {
    this.getFeedStore().clearAll()
  }

  /**
   * Resets the notification service state.
   * Used in tests/storybook to ensure clean state between stories.
   */
  reset() {
    this.closeAll()
  }
}

export const notificationService = new NotificationService()

export const Notification = {
  show: (options: FeedNotificationOptions) => notificationService.show(options),
  success: (options: FeedNotificationOptions | string) =>
    notificationService.success(options),
  error: (options: FeedNotificationOptions | string) =>
    notificationService.error(options),
  warning: (options: FeedNotificationOptions | string) =>
    notificationService.warning(options),
  info: (options: FeedNotificationOptions | string) =>
    notificationService.info(options),
  updateNotification: (
    id: string,
    updates: { message?: string; title?: string }
  ) => notificationService.updateNotification(id, updates),
  closeAll: () => notificationService.closeAll(),
  reset: () => notificationService.reset(),
} as const

export default Notification
